"use client";

import { motion } from "framer-motion";
import { useMemo, useState } from "react";
import { cn } from "@/lib/cn";
import { EASE } from "@/lib/motion";
import { useSportsbookBets } from "@/lib/api/hooks";
import { betIsSport, type SportsbookBet } from "@/lib/api/mappers";
import { Icon } from "@/components/ui/Icon";
import { Avatar, Chip, Panel, PanelHeader } from "@/components/ui/primitives";
import { SectionEmpty } from "@/components/ui/states";

/* Real slips other people have placed, from `/bets/sportsbook`.

   The renderer above owns betting; this owns proof that it is wired to the
   same platform the casino runs on. Every field here is one the API returns —
   there is no market we invented and no fixture we made up, which is why a
   leg with nothing but a sport still renders as just that sport.

   The server paints the first frame and the browser keeps it moving, the same
   arrangement the casino feeds use. Fifteen seconds rather than five: a
   betslip settles when a match does, not several times a minute. */

const REFRESH_MS = 15000;

const ALL = "All sports";

export function SportsbookBets({ initial }: { initial: SportsbookBet[] }) {
  const { bets } = useSportsbookBets({ limit: 20, refetchMs: REFRESH_MS, initial });
  const [sport, setSport] = useState(ALL);

  const all = bets.length > 0 ? bets : initial;

  /* The sports on offer are read off the slips themselves rather than a list
     we keep. Nothing to maintain, and it can never name a sport the book has
     not actually taken a bet on. */
  const sports = useMemo(
    () => [...new Set(all.flatMap((bet) => bet.legs.map((leg) => leg.sport)).filter(Boolean))].sort(),
    [all],
  );

  const rows = sport === ALL ? all : all.filter((bet) => betIsSport(bet, sport));

  return (
    <Panel>
      <PanelHeader
        icon="target"
        title="Latest sports bets"
        action={
          <span className="flex items-center gap-[6px] text-[12px] font-bold leading-[1.6] text-fg-subtle">
            <span className="glow-highlight h-[6px] w-[6px] rounded-full bg-highlight" />
            Live
          </span>
        }
      />

      {sports.length > 1 && (
        <div className="flex flex-wrap gap-[8px] border-t border-border p-[16px]">
          {[ALL, ...sports].map((label) => (
            <button
              key={label}
              type="button"
              onClick={() => setSport(label)}
              aria-pressed={sport === label}
              className={cn(
                "h-[40px] rounded-rounded px-[20px] text-[12px] font-bold leading-[1.6] transition-colors duration-200",
                sport === label ? "bg-brand text-fg-on-primary" : "bg-elevation-2 text-fg-subtle hover:text-fg",
              )}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <div className="border-t border-border">
        {rows.length === 0 ? (
          <SectionEmpty
            icon="target"
            title={sport === ALL ? "No sports bets settled yet" : `No ${sport} bets settled yet`}
            body="Slips appear here as soon as anyone places one."
          />
        ) : (
          <ul className="flex flex-col gap-[8px] p-[16px]">
            {rows.map((bet, index) => (
              <motion.li
                key={bet.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, ease: EASE, delay: Math.min(index, 8) * 0.04 }}
              >
                <Slip bet={bet} />
              </motion.li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  );
}

function Slip({ bet }: { bet: SportsbookBet }) {
  return (
    <article className="flex flex-col gap-[12px] rounded-lg border border-border bg-elevation-2 p-[14px]">
      <header className="flex flex-wrap items-center gap-[8px]">
        <Avatar name={bet.player} size={20} />
        <span className="text-[12px] font-bold leading-[1.6]">{bet.player}</span>
        <span className="text-[12px] leading-[1.6] text-fg-subtle">{bet.time}</span>

        {bet.live && (
          <Chip tone="highlight">
            <span className="glow-highlight h-[6px] w-[6px] rounded-full bg-highlight" />
            In-play
          </Chip>
        )}

        <Chip>{bet.legCount === 1 ? "Single" : `${bet.legCount}-fold`}</Chip>

        <span className="ml-auto flex items-center gap-[8px]">
          {bet.odds && <Chip className="tabular-nums">{bet.odds}</Chip>}
          <Status status={bet.status} outcome={bet.outcome} />
        </span>
      </header>

      <ul className="flex flex-col gap-[8px]">
        {bet.legs.map((leg) => (
          <li key={leg.id} className="flex flex-wrap items-baseline gap-x-[8px] gap-y-[4px]">
            <span className="text-[14px] font-bold leading-[1.6]">{leg.fixture || leg.sport || "Selection"}</span>
            {leg.market && (
              <span className="text-[12px] leading-[1.6] text-fg-muted">
                {leg.market}
                {leg.selection && <span className="text-fg"> · {leg.selection}</span>}
              </span>
            )}
            {leg.odds && (
              <span className="text-[12px] font-bold leading-[1.6] tabular-nums text-fg-subtle">@ {leg.odds}</span>
            )}
            {leg.tournament && (
              <span className="w-full text-[12px] leading-[1.6] text-fg-subtle">
                {leg.sport}
                {leg.sport && " · "}
                {leg.tournament}
              </span>
            )}
          </li>
        ))}
      </ul>

      <footer className="flex flex-wrap items-center gap-x-[20px] gap-y-[4px] border-t border-border pt-[12px]">
        <Amount label="Stake" value={bet.stake} />
        <Amount label={bet.settled ? "Returned" : "To return"} value={bet.returns} />
      </footer>
    </article>
  );
}

function Amount({ label, value }: { label: string; value: string }) {
  return (
    <span className="flex items-baseline gap-[6px]">
      <span className="text-[12px] font-bold uppercase tracking-[0.06em] leading-[1.6] text-fg-subtle">{label}</span>
      <span className="text-[14px] font-bold leading-[1.6] tabular-nums">{value}</span>
    </span>
  );
}

/* The provider names the status and may add to the list, so the word is shown
   as it came and only the tone is ours — taken from whether the slip paid more
   than it cost, which needs no vocabulary to stay right.

   An open slip gets no tone of its own: nothing has been won or lost yet, and
   the footer already says "To return" rather than "Returned". */
const TONE = {
  up: "success",
  down: "outline",
  even: "neutral",
  open: "neutral",
} as const;

function Status({ status, outcome }: { status: string; outcome: SportsbookBet["outcome"] }) {
  return (
    <Chip tone={TONE[outcome]} className="capitalize">
      {outcome === "up" && <Icon name="check" size={14} />}
      {status}
    </Chip>
  );
}
